import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, PencilSquareIcon, PlayCircleIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import Layout from '../components/layout/Layout';
import { StatusBadge, EstadoBadge } from '../components/ui/Badge';
import Spinner from '../components/ui/Spinner';
import ModalStatus, { proximoStatus, statusAnterior } from '../components/ui/ModalStatus';
import { formatCurrency, formatDate } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';

const Detalhe = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [produto, setProduto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fotoAtiva, setFotoAtiva] = useState(0);
  // 'foto-{idx}' ou 'video'
  const [midiaSelecionada, setMidiaSelecionada] = useState('foto-0');

  // Modal de status
  const [modal, setModal]     = useState({ open: false, novoStatus: null });
  const [salvando, setSalvando] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '');

  useEffect(() => {
    const fetchProduto = async () => {
      try {
        const res = await api.get(`/produtos/${id}`);
        setProduto(res.data);
      } catch (err) {
        if (err.response?.status === 404) navigate('/catalogo', { replace: true });
      } finally {
        setLoading(false);
      }
    };
    fetchProduto();
  }, [id, navigate]);

  const confirmarStatus = async () => {
    setSalvando(true);
    try {
      await api.patch(`/produtos/${id}/status`, { status: modal.novoStatus });
      setProduto((prev) => ({ ...prev, status: modal.novoStatus }));
      setModal({ open: false, novoStatus: null });
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-20"><Spinner size="lg" /></div>
      </Layout>
    );
  }

  if (!produto) return null;

  const proximo  = proximoStatus(produto.status);
  const anterior = statusAnterior(produto.status);

  return (
    <Layout>
      {/* Navegação */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="btn-secondary px-3 py-2" aria-label="Voltar">
          <ArrowLeftIcon className="w-4 h-4" />
        </button>
        <span className="text-sm text-gray-500">
          <Link to="/catalogo" className="hover:text-gray-900">Catálogo</Link>
          {' / '}
          <span className="text-gray-900 font-medium">{produto.nome}</span>
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Galeria de fotos + vídeo */}
        <div>
          {/* Mídia principal */}
          <div className="aspect-[4/3] bg-gray-100 rounded-xl overflow-hidden mb-3">
            {midiaSelecionada === 'video' && produto.video_url ? (
              <video
                src={`${API_BASE}${produto.video_url}`}
                controls
                playsInline
                autoPlay
                className="w-full h-full object-contain bg-black"
              />
            ) : produto.fotos?.length > 0 ? (
              <img
                src={`${API_BASE}${produto.fotos[parseInt(midiaSelecionada.replace('foto-', ''))]?.url}`}
                alt={produto.nome}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-300 text-sm">
                Sem fotos cadastradas
              </div>
            )}
          </div>

          {/* Miniaturas — fotos + vídeo */}
          {(produto.fotos?.length > 1 || (produto.fotos?.length >= 1 && produto.video_url) || produto.video_url) && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {/* Thumbnails das fotos */}
              {produto.fotos?.map((foto, idx) => (
                <button
                  key={foto.id}
                  onClick={() => setMidiaSelecionada(`foto-${idx}`)}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors
                    ${midiaSelecionada === `foto-${idx}` ? 'border-gray-900' : 'border-transparent'}`}
                >
                  <img src={`${API_BASE}${foto.url}`} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}

              {/* Thumbnail do vídeo */}
              {produto.video_url && (
                <button
                  onClick={() => setMidiaSelecionada('video')}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors
                    flex items-center justify-center bg-gray-900
                    ${midiaSelecionada === 'video' ? 'border-gray-900' : 'border-transparent'}`}
                >
                  <PlayCircleIcon className="w-8 h-8 text-white" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Informações */}
        <div>
          {/* Título + botão editar (admin) */}
          <div className="flex items-start justify-between gap-3 mb-3">
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">{produto.nome}</h1>
            {isAdmin && (
              <Link to={`/admin/produtos/${produto.id}`} className="btn-secondary flex-shrink-0">
                <PencilSquareIcon className="w-4 h-4" />
                Editar
              </Link>
            )}
          </div>

          {/* Badges de status e estado */}
          <div className="flex items-center gap-2 mb-5 flex-wrap">
            <StatusBadge status={produto.status} />
            <EstadoBadge estado={produto.estado} />
          </div>

          {/* Botões de alteração de status */}
          {(proximo || anterior) && (
            <div className="flex gap-2 mb-6">
              {proximo && (
                <button
                  onClick={() => setModal({ open: true, novoStatus: proximo })}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors
                    ${proximo === 'Reservado'
                      ? 'bg-amber-500 hover:bg-amber-600 text-white'
                      : 'bg-gray-900 hover:bg-black text-white'
                    }`}
                >
                  {proximo === 'Reservado' ? 'Reservar produto' : 'Marcar como vendido'}
                </button>
              )}
              {/* Desfazer: de Vendido só admin pode; de Reservado qualquer um */}
              {anterior && (produto.status !== 'Vendido' || isAdmin) && (
                <button
                  onClick={() => setModal({ open: true, novoStatus: anterior })}
                  className="flex-1 py-2.5 rounded-lg text-sm font-medium bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  ↩ Desfazer
                </button>
              )}
            </div>
          )}

          {/* Preços */}
          {produto.preco_mostruario && (
            <div className="card p-4 mb-6">
              <p className="text-xs text-gray-500 mb-1">Preço mostruário</p>
              <p className="text-3xl font-bold text-gray-900">
                {formatCurrency(produto.preco_mostruario)}
              </p>
              {produto.preco_original && (
                <p className="text-sm text-gray-400 mt-1 line-through">
                  De {formatCurrency(produto.preco_original)}
                </p>
              )}
            </div>
          )}

          {/* Detalhes */}
          <div className="space-y-3">
            <Row label="Categoria"  value={produto.categoria_nome} />
            <Row label="Marca"      value={produto.marca_nome} />
            <Row label="Tamanho"    value={produto.tamanho} />
            <Row label="Dimensões"  value={produto.dimensoes} />
            <Row label="Código"     value={produto.codigo} />
            <Row label="Cadastrado" value={formatDate(produto.criado_em)} />
          </div>

          {produto.descricao && (
            <div className="mt-6">
              <p className="text-sm font-medium text-gray-700 mb-1">Descrição</p>
              <p className="text-sm text-gray-600 leading-relaxed">{produto.descricao}</p>
            </div>
          )}

          {produto.observacoes && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-xs font-medium text-yellow-800 mb-1">Observações</p>
              <p className="text-sm text-yellow-900">{produto.observacoes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmação */}
      <ModalStatus
        open={modal.open}
        onClose={() => !salvando && setModal({ open: false, novoStatus: null })}
        nomeProduto={produto.nome}
        novoStatus={modal.novoStatus}
        onConfirmar={confirmarStatus}
        loading={salvando}
      />
    </Layout>
  );
};

const Row = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-50">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900">{value}</span>
    </div>
  );
};

export default Detalhe;
